import { useState } from 'react';
import { Button, Input, Modal } from '@immo/ui-kit';
import { useTranslations } from '@/i18n/use-translations';

type DialogState = {
  readonly busy: boolean;
  readonly error: string | null;
  readonly onClose: () => void;
};

function ErrorLine({ error }: { readonly error: string | null }) {
  if (error === null) return null;

  return (
    <p role="alert" className="mt-3 text-sm text-danger-600">
      {error}
    </p>
  );
}

const PERCENT_RANGE = { min: 1, max: 100 } as const;

/** The whole percent in range as a 0–1 fraction, or `null` for a blank, fractional or out-of-range draft. */
export function parseMaxWeeklyPercent(draft: string): number | null {
  const trimmed = draft.trim();

  if (!/^\d+$/.test(trimmed)) return null;

  const value = Number(trimmed);

  return value >= PERCENT_RANGE.min && value <= PERCENT_RANGE.max ? value / 100 : null;
}

/**
 * The account's weekly cap, edited as a percent and sent as the 0–1 fraction
 * the contract carries. Mounted fresh per open, so the draft seeds once.
 */
export function MaxWeeklyDialog({
  accountLabel,
  current,
  busy,
  error,
  onClose,
  onSave,
}: DialogState & {
  readonly accountLabel: string;
  readonly current: number;
  readonly onSave: (maxWeekly: number) => void;
}) {
  const t = useTranslations('pages.admin.fleet-accounts');
  const [draft, setDraft] = useState(String(Math.round(current * 100)));
  const [rangeError, setRangeError] = useState(false);

  const save = () => {
    const maxWeekly = parseMaxWeeklyPercent(draft);

    if (maxWeekly === null) {
      setRangeError(true);

      return;
    }

    onSave(maxWeekly);
  };

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="sm"
      title={t('max-weekly-dialog.title', { label: accountLabel })}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {t('cancel-cta')}
          </Button>
          <Button type="button" onClick={save} disabled={busy}>
            {t('max-weekly-dialog.save-cta')}
          </Button>
        </>
      }
    >
      <div className="flex items-center gap-2">
        <Input
          className="font-mono text-base-sm"
          inputMode="numeric"
          autoFocus
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setRangeError(false);
          }}
          disabled={busy}
          invalid={rangeError}
          aria-label={t('max-weekly-dialog.field')}
        />
        <span aria-hidden className="text-base-sm text-text-secondary">
          %
        </span>
      </div>
      {rangeError && (
        <p className="mt-2 text-sm text-danger-600">
          {t('max-weekly-dialog.range-error', { min: String(PERCENT_RANGE.min), max: String(PERCENT_RANGE.max) })}
        </p>
      )}
      <p className="mt-3 text-sm text-text-tertiary">{t('max-weekly-dialog.hint')}</p>
      <ErrorLine error={error} />
    </Modal>
  );
}
